import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';

/** Why the service returned a table with no numbers in it. */
export const SUGGESTION_SKIP_REASON = {
  invalidServicePeriod: 'InvalidServicePeriod',
  propertyNotFound: 'PropertyNotFound',
  submeterReadingsRequired: 'SubmeterReadingsRequired',
  utilityNotConfigured: 'UtilityNotConfigured'
};
const SKIP_MESSAGES = {
  [SUGGESTION_SKIP_REASON.invalidServicePeriod]: 'The service period runs backwards, so there is nothing to split.',
  [SUGGESTION_SKIP_REASON.propertyNotFound]: 'This property is no longer available on the company.',
  [SUGGESTION_SKIP_REASON.submeterReadingsRequired]: 'This utility is allocated by submeter, so the charges come from meter readings rather than from the bill total.',
  [SUGGESTION_SKIP_REASON.utilityNotConfigured]: 'This utility is not configured on the property.'
};
export const ALLOCATION_METHOD_LABELS = {
  By50Occupancy50SquareFootage: '50% occupancy / 50% square footage',
  ByNumberOfOccupants: 'By number of occupants',
  ByOccupancy: 'By occupancy',
  ByPercentageShare: 'By percentage share',
  BySquareFootage: 'By square footage',
  BySubmeter: 'By submeter',
  ByUnit: 'By unit'
};

/** One suggestion belongs to one property and utility, which is also how the response is keyed. */
export function getSuggestionKey(propertyId, utilityId) {
  return propertyId && utilityId ? "".concat(propertyId, ":").concat(utilityId) : '';
}
export function toDateOnly(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * The request the service needs for one table, or null while the inputs are still incomplete: an
 * unresolved property, a missing amount or a half-entered date range is not worth a round trip.
 */
export function buildSuggestionItem(table) {
  const startDateOnly = toDateOnly(table.serviceStartDate);
  const endDateOnly = toDateOnly(table.serviceEndDate);
  const amount = Number(table.billAmount);
  if (!table.propertyId || !table.utilityId || !startDateOnly || !endDateOnly || !Number.isFinite(amount)) {
    return null;
  }
  return {
    amount,
    endDateOnly,
    // Left empty, the service applies the goodwill deduction configured on the property utility.
    goodwillDeduction: Number.isFinite(Number(table.gwdOverride)) ? Number(table.gwdOverride) : null,
    propertyId: table.propertyId,
    startDateOnly,
    utilityId: table.utilityId
  };
}
export function buildSuggestionItems(tables) {
  const items = [];
  const seen = new Set();
  tables.forEach(table => {
    const item = buildSuggestionItem(table);
    if (!item) return;

    // The same property/utility can appear twice on a bill; the service answers it once.
    const key = "".concat(getSuggestionKey(item.propertyId, item.utilityId), ":").concat(item.amount, ":").concat(item.startDateOnly, ":").concat(item.endDateOnly, ":").concat(item.goodwillDeduction);
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  });
  return items;
}
export function indexSuggestions(items) {
  return (items || []).reduce((acc, item) => {
    acc[getSuggestionKey(item.propertyId, item.utilityId)] = item;
    return acc;
  }, {});
}
export function collectResidentAccountIds(items) {
  const ids = (items || []).flatMap(item => (item.allocations || []).map(row => row.residentAccountId));
  return [...new Set(ids.filter(Boolean))];
}

/** Percentages are unknown until the service answers, and an unknown percentage is not zero. */
export function formatPercent(value) {
  return value === null || value === undefined ? '—' : "".concat(value, "%");
}
export function getSkipMessage(skipReason) {
  return SKIP_MESSAGES[skipReason] || 'This utility cannot be allocated from the bill total.';
}
export function hasOverlappingBills(table) {
  return ((table === null || table === void 0 ? void 0 : table.overlappingBills) || []).length > 0;
}

/**
 * Whether the allocation service is holding this table back over how far the bill rose on the last
 * one. It will not be approved until the comparison with the previous cycles has been reviewed, and
 * until that comparison is to hand there is nothing to say it has been.
 */
export function isAwaitingSpikeReview(table) {
  var _table$comparison;
  return (table === null || table === void 0 ? void 0 : table.allocationState) === BILL_ALLOCATION_STATE.needsReview && !(table !== null && table !== void 0 && (_table$comparison = table.comparison) !== null && _table$comparison !== void 0 && _table$comparison.isReviewed);
}
function formatOverlapPeriod(bill) {
  return "".concat(moment(bill.startDateOnly).format('MM/DD/YY'), " \u2013 ").concat(moment(bill.endDateOnly).format('MM/DD/YY'));
}

/**
 * Said about a period the property has already been billed for on this utility. The charges are
 * still shown, because the dates are what needs correcting, but they cannot be approved: allocating
 * them would bill residents twice for the same days, and saving the bill would be rejected anyway.
 */
export function getOverlapMessage(overlappingBills) {
  const bills = overlappingBills || [];
  const periods = bills.map(formatOverlapPeriod).join(', ');
  return bills.length > 1 ? "These service dates overlap bills already allocated for this utility (".concat(periods, "). Allocating them again would charge residents twice, so change the dates before approving.") : "These service dates overlap a bill already allocated for this utility (".concat(periods, "). Allocating it again would charge residents twice, so change the dates before approving.");
}
function getResidentLabel(row, getResidentName) {
  if (!row.residentId) return 'Vacant';
  return getResidentName(row.residentAccountId) || 'Not enrolled';
}

/**
 * Turns one suggested table from the service into the rows and totals the table renders. The numbers
 * are taken as they come: they are the same figures the allocation would charge, and recalculating
 * them here would only invent a second answer.
 */
export function mapSuggestionTable(suggestion) {
  var _suggestion$commonAre, _suggestion$deduction, _suggestion$goodwillD, _suggestion$recovery, _suggestion$allocated;
  let getResidentName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => '';
  if (!suggestion) return null;
  return {
    isAllocated: false,
    allocationMethod: suggestion.allocationMethod || null,
    cadPercent: (_suggestion$commonAre = suggestion.commonAreaDeduction) !== null && _suggestion$commonAre !== void 0 ? _suggestion$commonAre : 0,
    deductionPercent: (_suggestion$deduction = suggestion.deductions) !== null && _suggestion$deduction !== void 0 ? _suggestion$deduction : 0,
    gwdPercent: (_suggestion$goodwillD = suggestion.goodwillDeduction) !== null && _suggestion$goodwillD !== void 0 ? _suggestion$goodwillD : 0,
    isComputable: suggestion.isComputable !== false,
    overlappingBills: suggestion.overlappingBills || [],
    recoveryPercent: (_suggestion$recovery = suggestion.recovery) !== null && _suggestion$recovery !== void 0 ? _suggestion$recovery : 0,
    skipReason: suggestion.skipReason || null,
    totalBilling: (_suggestion$allocated = suggestion.allocated) !== null && _suggestion$allocated !== void 0 ? _suggestion$allocated : null,
    units: (suggestion.allocations || []).map(row => ({
      days: row.daysOccupied,
      isBillable: row.isBillable,
      occupancyFactor: row.occupancyFactor,
      occupants: row.occupantsCount,
      resident: getResidentLabel(row, getResidentName),
      total: row.allocationCharge,
      unit: row.unitName
    }))
  };
}

/**
 * Whether a saved allocation table has been written all the way through. Approving a table and
 * working its charges out are separate steps, so a table read in between comes back approved but
 * with no bill and no rows — figures that would read as an allocation of nothing. Such a read is
 * worth nothing to show and is better waited out.
 */
export function isAllocatedTableWritten(table) {
  if (!table) return false;
  // A skipped utility is finished with, and has no charges by definition.
  if (table.isSkipped) return true;
  return !!table.bill && (table.allocations || []).length > 0;
}

/**
 * The saved allocation table, in the shape the same table renders. The suggestion was written to
 * mirror this response, so the difference is only in where the goodwill deduction is read from —
 * the bill records the one that was applied — and in nothing being suggested any more.
 */
export function mapAllocatedTable(table) {
  var _ref, _table$bill$goodwillD, _table$bill;
  let getResidentName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => '';
  if (!isAllocatedTableWritten(table)) return null;
  const mapped = mapSuggestionTable(_objectSpread(_objectSpread({}, table), {}, {
    goodwillDeduction: (_ref = (_table$bill$goodwillD = (_table$bill = table.bill) === null || _table$bill === void 0 ? void 0 : _table$bill.goodwillDeduction) !== null && _table$bill$goodwillD !== void 0 ? _table$bill$goodwillD : table.defaultGoodwillDeduction) !== null && _ref !== void 0 ? _ref : 0,
    isComputable: true,
    overlappingBills: [],
    skipReason: null
  }), getResidentName);
  return _objectSpread(_objectSpread({}, mapped), {}, {
    isAllocated: true,
    isApproved: !!table.isApproved,
    isSkipped: !!table.isSkipped
  });
}